import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environment';
import { Device } from '../models/device.model';
import { normalizeApiArray } from '../utils/api-response.util';

@Injectable({
  providedIn: 'root'
})
export class DeviceService {
  private apiUrl = `${environment.apiUrl}/Device`;

  constructor(private http: HttpClient) { }

  getDevices(): Observable<Device[]> {
    return this.http.get<unknown>(this.apiUrl).pipe(
      map(response => normalizeApiArray<Device>(response))
    );
  }

  getDevice(id: number): Observable<Device> {
    return this.http.get<Device>(`${this.apiUrl}/${id}`);
  }

  createDevice(device: Partial<Device>): Observable<Device> {
    return this.http.post<Device>(this.apiUrl, device);
  }

  updateDevice(id: number, device: Partial<Device>): Observable<Device> {
    return this.http.put<Device>(`${this.apiUrl}/${id}`, device);
  }

  updateSIMAssignments(
    id: number,
    assignments: { primarySIMID: number | null; secondarySIMID: number | null }
  ): Observable<Device> {
    return this.http.put<Device>(`${this.apiUrl}/${id}/sim-assignments`, assignments);
  }

  deleteDevice(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${id}`);
  }

  assignToVehicle(deviceId: number, vehicleId: number): Observable<Device> {
    return this.http.patch<Device>(`${this.apiUrl}/${deviceId}/assign`, { vehicleId });
  }

  unassignFromVehicle(deviceId: number): Observable<Device> {
    return this.http.patch<Device>(`${this.apiUrl}/${deviceId}/unassign`, {});
  }

  getAvailableDevices(): Observable<Device[]> {
    return this.getDevices().pipe(
      map(devices => devices.filter(device => !device.vehicleID))
    );
  }

  getDeviceStatus(deviceId: number): Observable<{ online: boolean; lastSeen: string }> {
    return this.http.get<{ online: boolean; lastSeen: string }>(
      `${this.apiUrl}/${deviceId}/status`
    );
  }

  sendCommand(deviceId: number, command: string): Observable<{ sent: boolean }> {
    return this.http.post<{ sent: boolean }>(
      `${this.apiUrl}/${deviceId}/command`,
      { command }
    );
  }
}
